<script setup>
// ========================================
// Imports
// ========================================
import { useDisplay } from 'vuetify'
import { refDebounced, useStorage } from '@vueuse/core'
import TabHeader from '@/components/TabHeader.vue'
import TablePaginationComplete from '@/components/pagination/TablePaginationComplete.vue'
import OpinionCard from '@/components/opinions/OpinionCard.vue'
import { showErrorToast } from '@/utils/toast'

// Keeps "Communication" highlighted in the menu
definePage({ meta: { navActiveLink: 'communication' } })

// ========================================
// Composables & Stores
// ========================================
const { t } = useI18n()
const { mobile, mdAndDown } = useDisplay()
const route = useRoute()
const router = useRouter()

// ========================================
// State
// ========================================
const search = ref('')
// Switches are remembered between visits
const onlyWithComment = useStorage('opinions-only-with-comment', false, localStorage, {
  listenToStorageChanges: false,
})
const onlyUnanswered = useStorage('opinions-only-unanswered', false, localStorage, {
  listenToStorageChanges: false,
})
const sortDir = ref('desc') // desc | asc - by opinion date
// "Rating details" switch - expands every card
const expandAll = useStorage('opinions-expand-all', false, localStorage, {
  listenToStorageChanges: false,
})
const toggledRows = ref({}) // opinion id -> expanded state flipped against expandAll
const page = ref(1)
const perPage = ref(10)
const opinions = ref([])
const average = ref(null) // from all opinions
const filteredAverage = ref(null) // BE sends it only when a filter is active
const respondAllowed = ref(false)
const meta = ref({})
const totalAll = ref(0) // meta.total of the last unfiltered request - the header shows all opinions
const isLoading = ref(true)

// ========================================
// Filters
// ========================================
const debouncedSearch = refDebounced(search, 400)

// Search goes to BE from 3 characters, like on Messages - shorter input lists every opinion.
// A computed, so typing the 1st and 2nd character keeps filterQuery (and the list) unchanged
const SEARCH_MIN_CHARS = 3
const searchQuery = computed(() => {
  const value = debouncedSearch.value?.trim() || ''

  return value.length >= SEARCH_MIN_CHARS ? value : ''
})

const sortOptions = computed(() => [
  { title: t('default_sort_newest'), value: 'desc', icon: 'ri-sort-desc' },
  { title: t('oldest'), value: 'asc', icon: 'ri-sort-asc' },
])

const hasActiveFilters = computed(() => !!(search.value || onlyWithComment.value || onlyUnanswered.value))

// Mobile: switches hidden behind a button - the badge counts the active ones
const showFilters = ref(false)
const panelFiltersCount = computed(() => [onlyWithComment.value, onlyUnanswered.value].filter(Boolean).length)

// Every request param but the page - any change starts again from page 1
const filterQuery = computed(() => {
  const query = {
    perPage: perPage.value,
    'orders[0][column]': 'date',
    'orders[0][direction]': sortDir.value,
  }
  if (searchQuery.value) query.search = searchQuery.value
  if (onlyWithComment.value) query.onlyWithComment = true
  if (onlyUnanswered.value) query.onlyUnanswered = true

  return query
})

const clearFilters = () => {
  search.value = ''
  onlyWithComment.value = false
  onlyUnanswered.value = false
}

// ========================================
// Opinions
// ========================================
let lastRequestId = 0

const fetchOpinions = async () => {
  const requestId = ++lastRequestId
  const query = { ...filterQuery.value, page: page.value }
  isLoading.value = true

  try {
    const response = await $backend('/opinions/index', { method: 'GET', query })
    if (requestId !== lastRequestId || !response?.success) return

    opinions.value = response.data?.opinions || []
    average.value = response.data?.average ?? null
    filteredAverage.value = response.data?.filteredAverage ?? null
    respondAllowed.value = !!response.data?.respondAllowed
    meta.value = response.meta || {}
    if (!query.search && !query.onlyWithComment && !query.onlyUnanswered) totalAll.value = meta.value.total || 0
    toggledRows.value = {}
  } catch (e) {
    // aborted or failed - $backend already showed the error
  } finally {
    if (requestId === lastRequestId) isLoading.value = false
  }
}

// The switches are remembered, so the page may open filtered - the header still needs the count of all opinions
const fetchTotalAll = async () => {
  try {
    const response = await $backend('/opinions/index', {
      method: 'GET',
      query: { perPage: 1, page: 1, 'orders[0][column]': 'date', 'orders[0][direction]': 'desc' },
      disableAbortController: true, // runs alongside the list request to the same endpoint
    })
    if (response?.success) totalAll.value = response.meta?.total || 0
  } catch (e) {
    // $backend already showed the error
  }
}

watch(filterQuery, () => {
  if (page.value === 1) fetchOpinions()
  else page.value = 1 // the page watcher fetches
})

const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' })

watch(page, () => {
  fetchOpinions()
  scrollToTop()
})

// A new page size goes back to page 1 - scrolls even when it was already there
watch(perPage, scrollToTop)

// Toggling all resets the single-card overrides
watch(expandAll, () => { toggledRows.value = {} })

// ========================================
// Cards
// ========================================
const isOpen = id => expandAll.value !== !!toggledRows.value[id]
const toggleRow = id => { toggledRows.value[id] = !toggledRows.value[id] }

// ========================================
// Opinion dialog - opened by ?opinion=<id>, e.g. from the opinions and messages menu
// ========================================
const shownOpinion = ref(null)
const shownRespondAllowed = ref(false)
const shownCard = ref(null) // its saveReply backs the footer button

const closeOpinion = () => {
  const { opinion, ...query } = route.query

  router.replace({ query })
}

const fetchShownOpinion = async id => {
  try {
    const response = await $backend('/opinions/show', { method: 'GET', query: { id } })
    if (String(route.query.opinion) !== String(id)) return // another opinion was asked for meanwhile

    if (!response?.success) {
      showErrorToast(response?.message || t('fetch_errors.response_error'))
      closeOpinion()

      return
    }
    shownOpinion.value = response.data?.opinion
    shownRespondAllowed.value = !!response.data?.respondAllowed
  } catch (e) {
    // $backend already showed the error - an aborted request belongs to a newer one, which keeps the dialog
    if (String(route.query.opinion) === String(id)) closeOpinion()
  }
}

watch(() => route.query.opinion, id => {
  shownOpinion.value = null
  if (id) fetchShownOpinion(id)
}, { immediate: true })

// The response shows up in the dialog and in the list beneath it
const onShownReplied = () => {
  fetchShownOpinion(route.query.opinion)
  fetchOpinions()
}

onMounted(() => {
  fetchOpinions()
  if (hasActiveFilters.value) fetchTotalAll()
})
</script>

<template>
  <TabHeader
    :title="$t('opinions')"
    :breadcrumbs="[
      { title: $t('communication'), to: '/communication' },
      { title: $t('opinions'), to: '/communication/opinions' },
    ]"
  >
    <!-- 👉 Summary of all opinions -->
    <div
      v-if="mobile"
      class="d-flex align-baseline ga-3 ml-auto text-body-2 text-medium-emphasis"
    >
      <span><b class="text-h6 text-high-emphasis">{{ average ?? '–' }}</b> {{ $t('opinions_page.average_short') }}</span>
      <span><b class="text-h6 text-high-emphasis">{{ totalAll }}</b> {{ $t('opinions_page.opinions_word', totalAll) }}</span>
    </div>
    <div
      v-else
      class="d-flex align-center ga-5 ml-auto"
    >
      <div class="d-flex align-center ga-2">
        <span class="text-h4 font-weight-medium">{{ average ?? '–' }}</span>
        <div class="d-flex flex-column text-caption summary-label">
          <span class="font-weight-medium text-high-emphasis">{{ $t('opinions_page.average_label') }}</span>
          <span class="text-medium-emphasis">{{ $t('opinions_page.average_hint') }}</span>
        </div>
      </div>
      <VDivider
        vertical
        class="align-self-stretch"
      />
      <div class="d-flex align-center ga-2">
        <span class="text-h4 font-weight-medium">{{ totalAll }}</span>
        <div class="d-flex flex-column text-caption summary-label">
          <span class="font-weight-medium text-high-emphasis">{{ $t('opinions_page.total_label') }}</span>
          <span class="text-medium-emphasis">{{ $t('opinions_page.total_hint') }}</span>
        </div>
      </div>
    </div>

    <!-- Same as on /communication/history -->
    <span :class="mobile ? 'ml-3' : 'ml-6'">
      <VBtn
        variant="outlined"
        color="secondary"
        :size="mobile ? 'small' : 'default'"
        to="/communication"
      >
        <VIcon
          icon="ri-arrow-left-line"
          size="18"
          :class="mobile ? '' : 'mr-1'"
        />
        <span v-if="!mobile">{{ $t('back') }}</span>
      </VBtn>
      <VTooltip
        v-if="!mobile"
        activator="parent"
      >
        {{ $t('back') }}
      </VTooltip>
    </span>
  </TabHeader>

  <VContainer
    class="mb-4"
    :class="{
      'px-0': mdAndDown,
      'px-15': !mdAndDown,
    }"
    fluid
  >
    <!-- Same container as /statistics/visits -->
    <VCard
      variant="elevated"
      :class="mobile ? 'mx-n4 px-2' : ''"
    >
      <!-- 👉 Filters - on mobile the switches are behind the "Filters" button -->
      <div
        class="d-flex flex-wrap align-center"
        :class="mobile ? 'ga-3 py-4 px-1' : 'ga-4 pa-4 pt-6'"
      >
        <VTextField
          v-model="search"
          :placeholder="$t('type_at_least_3chars')"
          :label="$t('opinions_page.search_placeholder')"
          prepend-inner-icon="ri-search-line"
          color="secondary"
          variant="outlined"
          density="compact"
          clearable
          hide-details
          :class="mobile ? 'flex-1-1-0' : 'search-field flex-0-0-auto'"
        />

        <template v-if="mobile">
          <VBtn
            variant="outlined"
            :color="showFilters || panelFiltersCount ? 'primary' : 'secondary'"
            :active="showFilters"
            prepend-icon="ri-equalizer-line"
            class="flex-0-0-auto"
            style="min-height: 40px;"
            @click="showFilters = !showFilters"
          >
            {{ $t('filters') }}
            <VBadge
              v-if="panelFiltersCount"
              :content="panelFiltersCount"
              color="primary"
              class="ml-2"
              inline
            />
          </VBtn>

          <template v-if="showFilters">
            <VCard
              variant="flat"
              border
              class="w-100 px-3 py-1"
            >
              <VSwitch
                v-model="onlyWithComment"
                :label="$t('opinions_page.only_with_comment')"
                color="primary"
                hide-details
              />
              <VSwitch
                v-model="onlyUnanswered"
                :label="$t('opinions_page.only_unanswered')"
                color="primary"
                hide-details
              />
              <VSwitch
                v-model="expandAll"
                :label="$t('opinions_page.rating_details')"
                color="primary"
                hide-details
              />
            </VCard>
            <VBtn
              variant="outlined"
              :disabled="!hasActiveFilters"
              :color="hasActiveFilters ? 'primary' : 'gray'"
              prepend-icon="ri-filter-off-line"
              block
              style="min-height: 40px;"
              @click="clearFilters"
            >
              {{ $t('clear_filters') }}
            </VBtn>
          </template>
        </template>

        <template v-else>
          <VSwitch
            v-model="onlyWithComment"
            :label="$t('opinions_page.only_with_comment')"
            color="primary"
            hide-details
            class="flex-grow-0"
          />
          <VSwitch
            v-model="onlyUnanswered"
            :label="$t('opinions_page.only_unanswered')"
            color="primary"
            hide-details
            class="flex-grow-0"
          />
          <VSpacer />
        </template>

        <!--        <span-->
        <!--          v-if="hasActiveFilters && filteredAverage !== null"-->
        <!--          class="text-body-2"-->
        <!--          :class="{ 'w-100 order-last': mobile }"-->
        <!--        >-->
        <!--          {{ $t('opinions_page.filtered_average') }}: <b>{{ filteredAverage }}</b>-->
        <!--          · {{ meta.total || 0 }} {{ $t('opinions_page.opinions_word', meta.total || 0) }}-->
        <!--        </span>-->

        <template v-if="!mobile">
          <VSwitch
            v-model="expandAll"
            :label="$t('opinions_page.rating_details')"
            color="primary"
            hide-details
            class="flex-grow-0"
          />
        </template>

        <!-- Same select as on /statistics/visits -->
        <VSelect
          v-model="sortDir"
          :label="$t('sorting')"
          :items="sortOptions"
          color="secondary"
          variant="outlined"
          density="compact"
          hide-details
          :class="mobile ? 'w-100' : 'flex-grow-0 ml-auto'"
          :style="mobile ? '' : 'min-inline-size: 230px'"
        >
          <template #item="{ props, item }">
            <VListItem v-bind="props">
              <template #prepend>
                <VIcon :icon="item.raw.icon" />
              </template>
            </VListItem>
          </template>
        </VSelect>
      </div>

      <!-- 👉 Opinions -->
      <div
        class="d-flex flex-column"
        :class="mobile ? 'ga-2 px-1 pb-4' : 'ga-3 px-4 pb-4'"
      >
        <!-- Every load shows the skeletons, like on /statistics/visits -->
        <template v-if="isLoading">
          <VSkeletonLoader
            v-for="n in 10"
            :key="n"
            type="list-item-avatar-three-line"
            class="rounded border"
          />
        </template>

        <div
          v-else-if="!opinions.length"
          class="py-12 text-center text-body-2 text-medium-emphasis"
        >
          {{ hasActiveFilters ? $t('opinions_page.empty_filtered') : $t('opinions_page.empty') }}
        </div>

        <template v-else>
          <OpinionCard
            v-for="opinion in opinions"
            :key="opinion.id"
            :opinion="opinion"
            :respond-allowed="respondAllowed"
            :open="isOpen(opinion.id)"
            @update:open="toggleRow(opinion.id)"
            @replied="fetchOpinions"
          />
        </template>
      </div>

      <TablePaginationComplete
        v-if="opinions.length"
        v-model="page"
        v-model:per-page="perPage"
        class="mb-4 mx-1"
        :length="Math.ceil((meta.total || 0) / perPage)"
        :page-size-items="[10, 20, 50]"
        show-results-summary
        :filtered-count="meta.total || 0"
        :total-count="totalAll"
        :what="$t('opinions_page.opinions_word', totalAll)"
      />
    </VCard>
  </VContainer>

  <!-- 👉 Opinion dialog -->
  <VDialog
    :model-value="!!route.query.opinion"
    :fullscreen="mobile"
    max-width="800"
    scrollable
    persistent
    @update:model-value="value => !value && closeOpinion()"
  >
    <VCard :class="{ 'rounded-0': mobile }">
      <div class="d-flex align-center justify-space-between ga-2 px-4 py-3 border-b">
        <div class="text-h5 d-flex align-center">
<!--          <VIcon-->
<!--            icon="ri-star-line"-->
<!--            class="mr-2"-->
<!--          />-->
          {{ $t('opinions_page.opinion') }}
        </div>
        <IconBtn @click="closeOpinion">
          <VIcon icon="ri-close-line" />
          <VTooltip activator="parent">
            {{ $t('close_window') }}
          </VTooltip>
        </IconBtn>
      </div>
      <VCardText class="pa-4">
        <VSkeletonLoader
          v-if="!shownOpinion"
          type="list-item-avatar-three-line"
          class="rounded border"
        />
        <OpinionCard
          v-else
          ref="shownCard"
          :opinion="shownOpinion"
          :respond-allowed="shownRespondAllowed"
          in-dialog
          @replied="onShownReplied"
        />
      </VCardText>
      <!-- Same footer as the visit dialog -->
      <VCardActions class="pt-2 border-t">
        <div class="d-flex justify-space-between flex-fill pa-2">
          <VBtn
            variant="outlined"
            color="secondary"
            @click="closeOpinion"
          >
            {{ $t('cancel') }}
          </VBtn>
          <VBtn
            v-if="shownCard?.canReply"
            variant="elevated"
            color="green"
            :disabled="!shownCard.canSave"
            :loading="shownCard.isSaving"
            @click="shownCard.saveReply()"
          >
            {{ $t('opinions_page.save_reply') }}
          </VBtn>
        </div>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<style scoped lang="scss">
.search-field {
  inline-size: 340px;
}

.summary-label {
  line-height: 1.3;
}
</style>
